import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('historioCart');
    return saved ? JSON.parse(saved) : [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('historioCart', JSON.stringify(cartItems));
  }, [cartItems]);

  const getCartItemKey = useCallback((item) => {
    if (!item) return '';
    const productId = item._id || item.id || item.product;
    return `${productId}`;
  }, []);

  const addToCart = useCallback((product, quantity = 1) => {
    setCartItems(prev => {
      const incomingKey = getCartItemKey(product);
      const existingItem = prev.find(item => getCartItemKey(item) === incomingKey);

      if (existingItem) {
        return prev.map(item =>
          getCartItemKey(item) === incomingKey
            ? { ...item, ...product, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prev, { ...product, quantity }];
    });

    setIsCartOpen(true);
  }, [getCartItemKey]);

  const removeFromCart = useCallback((cartKey) => {
    setCartItems(prev => prev.filter(item => getCartItemKey(item) !== cartKey));
  }, [getCartItemKey]);

  const updateQuantity = useCallback((cartKey, quantity) => {
    if (quantity <= 0) {
      setCartItems(prev => prev.filter(item => getCartItemKey(item) !== cartKey));
      return;
    }

    setCartItems(prev =>
      prev.map(item =>
        getCartItemKey(item) === cartKey
          ? { ...item, quantity }
          : item
      )
    );
  }, [getCartItemKey]);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [cartItems]);

  const getCartCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }, [cartItems]);

  const getShippingCost = useCallback((countryCode = 'HR', shippingRatesMap = null, subtotal = null, freeShippingThreshold = 70) => {
    const computedSubtotal = Number.isFinite(subtotal)
      ? subtotal
      : cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    if (computedSubtotal >= freeShippingThreshold) return 0;

    const normalizedCountry = String(countryCode || 'HR').trim().toUpperCase();
    if (shippingRatesMap && typeof shippingRatesMap === 'object') {
      const mapped = Number.parseFloat(shippingRatesMap[normalizedCountry]);
      if (Number.isFinite(mapped) && mapped >= 0) return mapped;
    }
    return 5;
  }, [cartItems]);

  const getRemainingForFreeShipping = useCallback((subtotal = null, freeShippingThreshold = 70) => {
    const computedSubtotal = Number.isFinite(subtotal)
      ? subtotal
      : cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    return Math.max(0, freeShippingThreshold - computedSubtotal);
  }, [cartItems]);

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    getShippingCost,
    getRemainingForFreeShipping,
    getCartItemKey,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
